import { importGhostApi } from '@/api-calls/sites/import-ghost'
import { ContainerWithSidebarAndNoTopbar } from '@/components/header/container'
import { Button, Input, Label } from '@blogging/ui'
import { useMutation } from '@ngtrvu/use-api'
import { FileJson, Loader2, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

export function SiteSettingsImportPage() {
  const { siteId } = useParams()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [ghostSiteUrl, setGhostSiteUrl] = useState<string>('')
  const [downloadImages, setDownloadImages] = useState<boolean>(true)

  const { mutate: importGhost, isPending: isImporting } = useMutation(
    importGhostApi,
    {
      resourceName: 'import',
      onSuccess: (data: any) => {
        toast.success(
          `Import successful! ${data?.postsCount || 0} posts imported.`,
        )
        setSelectedFile(null)
        setGhostSiteUrl('')
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to import content')
      },
    },
  )

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (!file.name.endsWith('.json')) {
        toast.error('Please select a JSON file')
        return
      }
      setSelectedFile(file)
    }
  }

  const handleImport = () => {
    if (!selectedFile) {
      toast.error('Please select a file to import')
      return
    }
    importGhost({
      siteId: siteId as string,
      file: selectedFile,
      ghostSiteUrl: ghostSiteUrl.trim() || undefined,
      downloadImages,
    })
  }

  return (
    <ContainerWithSidebarAndNoTopbar>
      <div className="flex flex-col space-y-8">
        <div className="flex flex-col space-y-2">
          <h1 className="text-3xl font-bold">Import Content</h1>
          <p className="text-muted-foreground">
            Import content from other platforms to your site.
          </p>
        </div>

        <div className="rounded-lg border bg-card p-6 space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Import from Ghost</h3>
            <p className="text-sm text-muted-foreground">
              Import your posts and pages from a Ghost CMS export file. Export
              your content from Ghost by going to Settings → Labs → Export
              content.
            </p>

            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="ghost-file">Ghost Export File (JSON)</Label>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Input
                      ref={fileInputRef}
                      id="ghost-file"
                      type="file"
                      accept=".json"
                      onChange={handleFileChange}
                      className="cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ghost-url">Ghost Site URL (Optional)</Label>
                <Input
                  id="ghost-url"
                  type="url"
                  placeholder="https://your-ghost-site.com"
                  value={ghostSiteUrl}
                  onChange={(e) => setGhostSiteUrl(e.target.value)}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Enter your Ghost site URL to replace __GHOST_URL__
                  placeholders in image URLs. This is required if your export
                  contains relative image URLs.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <input
                  id="download-images"
                  type="checkbox"
                  checked={downloadImages}
                  onChange={(e) => setDownloadImages(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-input accent-primary cursor-pointer"
                />
                <div className="space-y-1">
                  <Label htmlFor="download-images" className="cursor-pointer">
                    Download and re-upload images
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    When enabled, images are downloaded from your Ghost site and
                    stored in your own storage. Disable this to keep original
                    image URLs (images must remain publicly accessible).
                  </p>
                </div>
              </div>

              {selectedFile && (
                <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                  <FileJson className="h-8 w-8 text-primary" />
                  <div className="flex-1">
                    <p className="font-medium">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedFile(null)
                      if (fileInputRef.current) {
                        fileInputRef.current.value = ''
                      }
                    }}
                  >
                    Remove
                  </Button>
                </div>
              )}

              <Button
                onClick={handleImport}
                disabled={isImporting || !selectedFile}
                className="w-full sm:w-auto"
              >
                {isImporting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Import Content
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="border-t pt-6 space-y-4">
            <h4 className="font-medium">What gets imported?</h4>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                <span>Posts with their content, titles, and metadata</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                <span>Pages with their content and settings</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                <span>Tags and categories</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                <span>Authors (name, bio, avatar, website, and social links)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                <span>Images (uploaded to your storage when enabled)</span>
              </li>
            </ul>
          </div>

          <div className="border-t pt-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-medium text-yellow-800 mb-2">
                Before importing
              </h4>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Make sure you have a backup of your current content</li>
                <li>• Duplicate posts will be skipped based on their slug</li>
                <li>• Large files may take a few minutes to process</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </ContainerWithSidebarAndNoTopbar>
  )
}
